import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Zoom,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Popover,
  useTheme,
  alpha,
  useMediaQuery,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BusinessIcon from "@mui/icons-material/Business";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  getAllDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
} from "../../../Services/Services";

const DepartmentManagement = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form Modal
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deptName, setDeptName] = useState("");
  const [nameError, setNameError] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [multiDepts, setMultiDepts] = useState([{ name: "" }]);

  // Delete Popover
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllDepartments();
      if (response.success) {
        setDepartments(response.data || []);
      } else {
        throw new Error(response.message || "Failed to fetch");
      }
    } catch (err) {
      const msg = err.message || "Failed to load departments";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDeptName("");
    setNameError("");
    setIsEditMode(false);
    setEditId(null);
    setMultiDepts([{ name: "" }]);
  };

  const openAddModal = () => {
    resetForm();
    setOpenModal(true);
  };

  const openEditModal = (dept) => {
    setIsEditMode(true);
    setEditId(dept.id);
    setDeptName(dept.name);
    setNameError("");
    setOpenModal(true);
  };

  const handleSubmit = async () => {
    if (isEditMode) {
      // ── Edit Mode: Single department ──
      const name = deptName.trim();
      if (!name) {
        setNameError("Department name is required");
        return;
      }
      if (name.length < 2) {
        setNameError("Name must be at least 2 characters");
        return;
      }

      setFormLoading(true);
      try {
        const response = await updateDepartment(editId, { name });
        if (response.success) {
          await fetchDepartments();
          toast.success("Department updated!");
          setOpenModal(false);
          resetForm();
        } else {
          throw new Error(response.message || "Failed");
        }
      } catch (err) {
        if (err.response?.data?.errors?.name) {
          const nameErr = err.response.data.errors.name;
          setNameError(Array.isArray(nameErr) ? nameErr[0] : nameErr);
        } else {
          toast.error(err.message || "Failed to update");
        }
      } finally {
        setFormLoading(false);
      }
    } else {
      // ── Add Mode: Multiple departments ──
      const validDepts = multiDepts.filter((d) => d.name.trim().length >= 2);

      if (validDepts.length === 0) {
        setNameError("Please enter at least one department name (min 2 chars)");
        return;
      }

      setFormLoading(true);
      let successCount = 0;
      let failCount = 0;

      try {
        for (const dept of validDepts) {
          try {
            const response = await createDepartment({ name: dept.name.trim() });
            if (response.success) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (err) {
            failCount++;
            const errMsg = err.response?.data?.errors?.name;
            if (errMsg) {
              toast.error(
                `"${dept.name}": ${Array.isArray(errMsg) ? errMsg[0] : errMsg}`
              );
            }
          }
        }

        if (successCount > 0) {
          await fetchDepartments();
          toast.success(`${successCount} department(s) added successfully!`);
          setOpenModal(false);
          resetForm();
        }

        if (failCount > 0 && successCount === 0) {
          toast.error("Failed to add departments");
        }
      } finally {
        setFormLoading(false);
      }
    }
  };

  // ── Multi-add helpers ──
  const handleMultiChange = (index, value) => {
    const updated = [...multiDepts];
    updated[index].name = value;
    setMultiDepts(updated);
    if (value.trim()) setNameError("");
  };

  const addDeptRow = () => {
    if (multiDepts.length >= 10) {
      toast.warning("Maximum 10 departments at a time");
      return;
    }
    setMultiDepts([...multiDepts, { name: "" }]);
  };

  const removeDeptRow = (index) => {
    if (multiDepts.length <= 1) return;
    setMultiDepts(multiDepts.filter((_, i) => i !== index));
  };

  const handleDeleteClick = (e, id) => {
    setAnchorEl(e.currentTarget);
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const response = await deleteDepartment(deleteId);
      if (response.success) {
        await fetchDepartments();
        toast.info("Department deleted!");
      } else {
        throw new Error(response.message || "Failed");
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete");
    } finally {
      setAnchorEl(null);
      setDeleteId(null);
    }
  };

  const tableHeadBg = isDark ? "#252b38" : "#f0f4ff";
  const rowBg = (index) =>
    index % 2 === 0
      ? isDark ? "#20242e" : "#fafafa"
      : isDark ? "#1a1d26" : "#ffffff";

  if (loading) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress size={40} color="primary" />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100%", px: { xs: 1, md: 0 } }}>
      <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />

      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: { xs: 3, md: 1 },
          p: 1.5,
          position: "sticky",
          top: 0,
          zIndex: 1100,
          bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid",
          borderColor: "divider",
          boxShadow: isDark ? "0 2px 10px rgba(0,0,0,0.45)" : "0 1px 4px rgba(0,0,0,0.07)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BusinessIcon sx={{ color: "primary.main" }} />
          <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>
            Departments
          </Typography>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: "text.secondary",
              bgcolor: isDark ? alpha(theme.palette.common.white, 0.08) : "#f1f5f9",
              px: 1,
              py: 0.3,
              borderRadius: 1,
            }}
          >
            {departments.length}
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddModal}
          sx={{
            height: 32,
            fontSize: "0.75rem",
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 700,
          }}
        >
          Add Department
        </Button>
      </Box>

      {/* ── Error ── */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 1 }}>
          {error}
        </Alert>
      )}

      {/* ── Table ── */}
      {departments.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: 2,
            bgcolor: "background.paper",
            border: "1px dashed",
            borderColor: "divider",
            mt: 4,
          }}
        >
          <BusinessIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No departments yet</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: "0.85rem", mb: 2 }}>
            Create your first department to get started.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAddModal}>
            Add Department
          </Button>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
            backgroundImage: "none",
            boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "0.75rem",
                    bgcolor: `${tableHeadBg} !important`,
                    borderBottom: "2px solid",
                    borderColor: "primary.main",
                    width: 60,
                  }}
                >
                  S.No
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "0.75rem",
                    bgcolor: `${tableHeadBg} !important`,
                    borderBottom: "2px solid",
                    borderColor: "primary.main",
                  }}
                >
                  Department Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "0.75rem",
                    bgcolor: `${tableHeadBg} !important`,
                    borderBottom: "2px solid",
                    borderColor: "primary.main",
                    width: 100,
                    textAlign: "center",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {departments.map((dept, index) => (
                <Zoom in key={dept.id} style={{ transitionDelay: `${index * 30}ms` }}>
                  <TableRow
                    sx={{
                      bgcolor: rowBg(index),
                      "&:hover": {
                        bgcolor: isDark ? "#2c3242 !important" : "#e3f2fd !important",
                      },
                    }}
                  >
                    <TableCell sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      {dept.name}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit" arrow>
                        <IconButton
                          size="small"
                          onClick={() => openEditModal(dept)}
                          sx={{
                            color: "primary.main",
                            "&:hover": {
                              bgcolor: isDark
                                ? alpha(theme.palette.primary.main, 0.15)
                                : "#e3f2fd",
                            },
                          }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, dept.id)}
                          sx={{
                            color: "error.main",
                            "&:hover": {
                              bgcolor: isDark
                                ? alpha(theme.palette.error.main, 0.15)
                                : "#ffebee",
                            },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </Zoom>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Add/Edit Modal ── */}
            <Dialog
        open={openModal}
        onClose={() => {
          if (!formLoading) {
            setOpenModal(false);
            resetForm();
          }
        }}
        fullWidth
        maxWidth="sm"
        disableScrollLock
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1.1rem", pb: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                display: "grid",
                placeItems: "center",
                borderRadius: "10px",
                bgcolor: isEditMode
                  ? isDark ? alpha("#7c3aed", 0.18) : "#f5f3ff"
                  : isDark ? alpha(theme.palette.primary.main, 0.18) : "#eff6ff",
                color: isEditMode ? "#7c3aed" : "primary.main",
              }}
            >
              {isEditMode ? <EditOutlinedIcon /> : <AddIcon />}
            </Box>
            {isEditMode ? "Edit Department" : "Add Departments"}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {isEditMode ? (
            /* ── Edit Mode: Single Field ── */
            <TextField
              label="Department Name"
              value={deptName}
              onChange={(e) => {
                setDeptName(e.target.value);
                if (e.target.value.trim()) setNameError("");
              }}
              fullWidth
              required
              error={Boolean(nameError)}
              helperText={nameError}
              size="small"
              autoFocus
              placeholder="e.g. Production, Quality, HR"
              disabled={formLoading}
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": { borderRadius: "10px" },
              }}
            />
          ) : (
            /* ── Add Mode: Multiple Fields ── */
            <Box sx={{ mt: 1 }}>
              {nameError && (
                <Alert severity="error" sx={{ mb: 1, py: 0, fontSize: "0.75rem" }}>
                  {nameError}
                </Alert>
              )}

              {multiDepts.map((dept, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <TextField
                    label={`Department ${index + 1}`}
                    value={dept.name}
                    onChange={(e) => handleMultiChange(index, e.target.value)}
                    fullWidth
                    required
                    size="small"
                    autoFocus={index === 0}
                    placeholder={
                      ["Production", "Quality", "HR", "Maintenance", "Accounts"][index] ||
                      `Department ${index + 1}`
                    }
                    disabled={formLoading}
                    error={dept.name.trim().length > 0 && dept.name.trim().length < 2}
                    helperText={
                      dept.name.trim().length > 0 && dept.name.trim().length < 2
                        ? "Min 2 characters"
                        : ""
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    }}
                  />

                  {multiDepts.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeDeptRow(index)}
                      disabled={formLoading}
                      sx={{
                        color: "error.main",
                        flexShrink: 0,
                        "&:hover": {
                          bgcolor: isDark
                            ? alpha(theme.palette.error.main, 0.15)
                            : "#ffebee",
                        },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: "1.1rem" }} />
                    </IconButton>
                  )}
                </Box>
              ))}

              <Button
                size="small"
                startIcon={<AddIcon sx={{ fontSize: "0.9rem" }} />}
                onClick={addDeptRow}
                disabled={formLoading || multiDepts.length >= 10}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  color: "primary.main",
                  mt: 0.5,
                }}
              >
                Add More
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setOpenModal(false);
              resetForm();
            }}
            variant="outlined"
            disabled={formLoading}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={formLoading}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
          >
            {formLoading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : isEditMode ? (
              "Update"
            ) : (
              `Save ${multiDepts.filter((d) => d.name.trim().length >= 2).length || ""}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Popover ── */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setDeleteId(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark ? "0 8px 28px rgba(0,0,0,0.6)" : "0 4px 16px rgba(0,0,0,0.14)",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Delete this department?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setDeleteId(null);
              }}
            >
              No
            </Button>
            <Button color="error" variant="contained" size="small" onClick={confirmDelete}>
              Yes
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default DepartmentManagement;